

class ProductForm extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.buyBtn = this.form.querySelector('.btn-buy')
    this.variants = this.form.dataset.variants ? JSON.parse(this.form.dataset.variants) : []

    this.form.addEventListener('submit', event => this.onSubmit(event))

    // 标记：用户是否有过交互
    this.hasUserInteraction = false

    this.productOptions.forEach(elem => {
      elem.addEventListener('change', event => this.onChangeProductOption(event))
      
      // 标记用户交互
      elem.addEventListener('click', () => {
        this.hasUserInteraction = true
        
        // 隐藏对应选项的提示文本
        this.hideOptionHint(elem)
      }, { once: false })
      
      // 对于 radio 按钮，添加 click 事件处理已选中选项的点击
      // 这样点击已选中的默认选项时也能触发图片切换
      if (elem.type === 'radio') {
        elem.addEventListener('click', event => this.onClickProductOption(event))
      }
    })

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))

    // 防止过滤时的循环触发
    this.isFiltering = false
    
    // 标记：防止初始化时更新 URL
    this.isInitializing = true

    // 初始化时只在有 URL 参数时才选择变体
    if (this.variants && this.variants.length > 0) {
      setTimeout(() => {
        // 先检查 URL 参数
        const urlParams = new URLSearchParams(window.location.search)
        const variantId = urlParams.get('variant')
        
        if (variantId) {
          // 有 URL 参数，选择对应变体
          this.selectVariantFromURL()
        } else {
          // 没有 URL 参数
          
          // 检查是否只有一个变体（单变体产品）
          if (this.variants.length === 1) {
            // 单变体产品，自动选择并启用按钮
            const singleVariant = this.variants[0]
            this.querySelector('[name="id"]').value = singleVariant.id
            this.adjustPricing(singleVariant)
            this.adjustButtons(singleVariant)
          } else {
            // 多变体产品，不自动选择
            this.filterOptions()
            this.updateColorSwatchImages()
            
            // 设置初始状态：禁用按钮
            this.setNoVariantSelectedState()
          }
        }
        
        // 初始化完成，允许后续的 URL 更新
        setTimeout(() => {
          this.isInitializing = false
        }, 200)
      }, 100)
    }
  }

  // 设置未选择变体的初始状态
  setNoVariantSelectedState() {
    if (this.atcBtn) {
      this.atcBtn.setAttribute('disabled', 'disabled')
      this.atcBtn.innerHTML = window.theme.locales.product.select_options || 'Please select options'
    }
    if (this.buyBtn) {
      this.buyBtn.setAttribute('disabled', 'disabled')
    }
  }

  // 隐藏选项的"Please select"提示
  hideOptionHint(elem) {
    const optionContainer = elem.closest('li[data-option-position]')
    if (optionContainer) {
      const hint = optionContainer.querySelector('[data-option-hint]')
      if (hint) {
        hint.style.display = 'none'
      }
    }
  }

  // 显示选项的"Please select"提示
  showOptionHint(elem) {
    const optionContainer = elem.closest('li[data-option-position]')
    if (optionContainer) {
      const hint = optionContainer.querySelector('[data-option-hint]')
      if (hint) {
        hint.style.display = ''
      }
    }
  }

  // 从URL参数中选择变体
  selectVariantFromURL() {
    const urlParams = new URLSearchParams(window.location.search)
    const variantId = urlParams.get('variant')
    
    if (variantId && this.variants) {
      const variant = this.variants.find(v => v.id == variantId)
      
      if (variant && variant.available) {
        // 更新隐藏的variant ID输入框
        const variantInput = this.form.querySelector('[name="id"]')
        if (variantInput) {
          variantInput.value = variant.id
        }
        
        // 选中对应的选项
        variant.options.forEach((optionValue, index) => {
          const optionInputs = this.form.querySelectorAll(`[name="option${index + 1}"]`)
          
          optionInputs.forEach(input => {
            if (input.type === 'radio') {
              input.checked = (input.value === optionValue)
            } else if (input.type === 'select-one') {
              input.value = optionValue
            }
          })
        })
        
        // 标记这是从URL加载的变体，防止被filterOptions重置
        this.isUrlVariant = true
        
        // 更新价格和按钮状态
        this.adjustPricing(variant)
        this.adjustButtons(variant)
        
        // 触发变体变化事件
        window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
          detail: variant
        }))
        
        // 重新过滤选项以确保UI一致
        this.filterOptions()
      }
    }
  }

  // 处理点击已选中的 radio 选项（change 事件不会触发）
  onClickProductOption (event) {
    const elem = event.target
    
    // 只处理已经选中的 radio 按钮的点击
    if (elem.type === 'radio' && elem.checked) {
      // 获取当前选中的变体并派发事件，让图片切换到对应位置
      const selectedVariant = this.getCurrentSelectedVariant()
      if (selectedVariant) {
        window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
          detail: selectedVariant
        }))
      }
    }
  }

  // 获取当前选中的变体
  getCurrentSelectedVariant () {
    const selectedOptions = []

    this.productOptions.forEach(elem => {
      if (elem.type === 'radio' && elem.checked) {
        selectedOptions.push(elem.value)
      } else if (elem.type === 'select-one') {
        // 只有当值不为空时才添加
        if (elem.value !== '') {
          selectedOptions.push(elem.value)
        }
      }
    })

    // 如果选项数量不完整，返回 null
    const expectedOptionsCount = [...new Set(Array.from(this.productOptions).map(e => e.name))].length
    if (selectedOptions.length !== expectedOptionsCount) {
      return null
    }

    return this.variants.find(variant =>
      JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
    )
  }

  async onChangeProductOption (event) {
    event.preventDefault()

    const selectedOptions = []

    this.productOptions.forEach(elem => {
      if (elem.type === 'radio' && elem.checked) {
        selectedOptions.push(elem.value)
      } else if (elem.type === 'select-one') {
        selectedOptions.push(elem.value)
      }
    })

    // 检查是否所有选项都已选择
    const allOptionsSelected = selectedOptions.length > 0 && !selectedOptions.includes('') && !selectedOptions.includes(undefined)

    if (!allOptionsSelected) {
      // 如果没有选择完整的选项，设置为未选择状态
      this.setNoVariantSelectedState()
      this.filterOptions()
      this.updateColorSwatchImages()
      
      // 清空 variant ID
      this.querySelector('[name="id"]').value = ''
      
      // 检查每个选项组，如果没有选中任何值，显示提示
      const optionGroups = {}
      this.productOptions.forEach(elem => {
        const optionName = elem.name
        if (!optionGroups[optionName]) {
          optionGroups[optionName] = { elements: [], hasSelection: false }
        }
        optionGroups[optionName].elements.push(elem)
        
        if (elem.type === 'radio' && elem.checked) {
          optionGroups[optionName].hasSelection = true
        } else if (elem.type === 'select-one' && elem.value !== '') {
          optionGroups[optionName].hasSelection = true
        }
      })
      
      // 显示或隐藏提示
      Object.values(optionGroups).forEach(group => {
        if (group.elements.length > 0) {
          if (group.hasSelection) {
            this.hideOptionHint(group.elements[0])
          } else {
            this.showOptionHint(group.elements[0])
          }
        }
      })
      
      // 移除 URL 中的 variant 参数（仅在初始化完成且有用户交互后）
      if (!this.isInitializing && this.hasUserInteraction && this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.delete('variant')
        window.history.replaceState({}, '', url)
      }
      
      return
    }

    // console.log(selectedOptions)

    const selectedVariant = this.variants.find(variant =>
      JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
    )

    console.log(selectedVariant)

    // 在更新价格和按钮后，过滤选项以确保其他选项只显示可用的值
    this.filterOptions()
    
    // 更新颜色选项的缩略图
    this.updateColorSwatchImages()

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant)

    if (selectedVariant) {
      this.querySelector('[name="id"]').value = selectedVariant.id

      // 只在初始化完成且有用户交互后才更新 URL
      if (!this.isInitializing && this.hasUserInteraction && this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.set('variant', selectedVariant.id)
        window.history.replaceState({}, '', url)
      }

      window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
        detail: selectedVariant
      }))
    } else {
      // 如果没有找到匹配的变体（可能是无效组合）
      this.querySelector('[name="id"]').value = ''
      
      // 移除 URL 中的 variant 参数（仅在初始化完成且有用户交互后）
      if (!this.isInitializing && this.hasUserInteraction && this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.delete('variant')
        window.history.replaceState({}, '', url)
      }
    }
  }

  // 获取当前已选择的选项值（按选项索引顺序）
  getSelectedOptions () {
    // 按选项名称分组，确定选项索引
    const optionGroups = {}
    this.productOptions.forEach(elem => {
      const optionName = elem.name
      if (!optionGroups[optionName]) {
        optionGroups[optionName] = []
      }
      optionGroups[optionName].push(elem)
    })

    const optionNames = Object.keys(optionGroups)
    const selectedOptions = []

    // 按选项组顺序获取已选择的值
    optionNames.forEach(optionName => {
      const optionElements = optionGroups[optionName]
      let selectedValue = null

      optionElements.forEach(elem => {
        if (elem.type === 'radio' && elem.checked) {
          selectedValue = elem.value
        } else if (elem.type === 'select-one') {
          // 对于 select，只有当值不为空时才认为已选择
          selectedValue = elem.value !== '' ? elem.value : null
        }
      })

      selectedOptions.push(selectedValue)
    })

    return selectedOptions
  }

  // 检查某个选项值是否在当前已选择选项的组合下可用
  isOptionValueAvailable (optionIndex, optionValue, selectedOptions) {
    // 创建测试选项数组
    const testOptions = [...selectedOptions]
    testOptions[optionIndex] = optionValue

    // 检查是否存在匹配的变体
    return this.variants.some(variant => {
      // 检查变体的选项是否与测试选项匹配（只检查非null的值）
      for (let i = 0; i < testOptions.length; i++) {
        if (testOptions[i] !== null && variant.options[i] !== testOptions[i]) {
          return false
        }
      }
      return true
    })
  }

  // 根据已选择的选项过滤其他选项的可用值
  filterOptions () {
    if (!this.variants || this.variants.length === 0) return
    if (this.isFiltering) return // 防止循环触发

    this.isFiltering = true

    try {
      // 获取所有选项组，并确定它们的索引顺序
      const optionGroups = {}
      this.productOptions.forEach(elem => {
        const optionName = elem.name
        if (!optionGroups[optionName]) {
          optionGroups[optionName] = []
        }
        optionGroups[optionName].push(elem)
      })

      const optionNames = Object.keys(optionGroups)
      const currentSelectedOptions = this.getSelectedOptions()
      
      // 对每个选项组进行过滤
      optionNames.forEach((optionName, optionIndex) => {
        const optionElements = optionGroups[optionName]
        
        optionElements.forEach(elem => {
          const optionValue = elem.value
          // 创建测试选项数组，将当前选项值放入对应位置
          const testOptions = [...currentSelectedOptions]
          testOptions[optionIndex] = optionValue

          // 检查是否存在匹配的变体
          const isAvailable = this.variants.some(variant => {
            for (let i = 0; i < testOptions.length; i++) {
              if (testOptions[i] !== null && variant.options[i] !== testOptions[i]) {
                return false
              }
            }
            return true
          })

          if (elem.type === 'radio') {
            // 对于 radio 按钮，隐藏或显示父元素 li
            const parentLi = elem.closest('li')
            if (parentLi) {
              if (isAvailable) {
                parentLi.style.display = ''
              } else {
                parentLi.style.display = 'none'
                // 如果当前选中的 radio 不可用，取消选中（但不自动选择其他选项）
                // 除非这是从URL加载的变体
                if (elem.checked && !this.isUrlVariant) {
                  elem.checked = false
                }
              }
            }
          } else if (elem.type === 'select-one') {
            // 对于 select 下拉框的 option 元素
            if (isAvailable) {
              elem.style.display = ''
              elem.disabled = false
              elem.removeAttribute('hidden')
            } else {
              elem.style.display = 'none'
              elem.disabled = true
              elem.setAttribute('hidden', 'hidden')
              // 如果当前选中的 option 不可用，需要重置选择
              // 除非这是从URL加载的变体
              if (elem.selected && !this.isUrlVariant) {
                const select = elem.closest('select')
                if (select) {
                  // 重置为空选项（第一个选项应该是空的占位符）
                  select.value = ''
                  // 更新 selectedOptions
                  currentSelectedOptions[optionIndex] = null
                }
              }
            }
          }
        })
      })
      
      // 清除URL变体标记
      if (this.isUrlVariant) {
        this.isUrlVariant = false
      }
    } finally {
      this.isFiltering = false
    }
  }

  // 更新颜色选项的缩略图，根据当前已选择的其他选项
  updateColorSwatchImages () {
    // 获取当前已选择的选项
    const currentSelectedOptions = this.getSelectedOptions()
    
    // 查找所有颜色选项的容器
    const colorOptionLists = this.form.querySelectorAll('.product-options-visual ul[data-option-name]')
    
    colorOptionLists.forEach(optionList => {
      const optionName = optionList.dataset.optionName
      
      // 确定这个选项在选项数组中的位置
      let optionIndex = -1
      const optionGroups = {}
      this.productOptions.forEach(elem => {
        const name = elem.name
        if (!optionGroups[name]) {
          optionGroups[name] = []
        }
        optionGroups[name].push(elem)
      })
      const optionNames = Object.keys(optionGroups)
      optionNames.forEach((name, index) => {
        if (name === `option-${optionName}`) {
          optionIndex = index
        }
      })
      
      if (optionIndex === -1) return
      
      // 遍历该选项的所有值
      const optionItems = optionList.querySelectorAll('li[data-option-value]')
      optionItems.forEach(item => {
        const optionValue = item.dataset.optionValue
        const imageContainer = item.querySelector('[data-variant-image-container]')
        
        if (!imageContainer) return
        
        // 构建测试选项数组
        const testOptions = [...currentSelectedOptions]
        testOptions[optionIndex] = optionValue
        
        // 查找匹配的变体
        const matchingVariant = this.variants.find(variant => {
          for (let i = 0; i < testOptions.length; i++) {
            if (testOptions[i] !== null && variant.options[i] !== testOptions[i]) {
              return false
            }
          }
          return true
        })
        
        // 更新图片
        if (matchingVariant && matchingVariant.featured_image) {
          const imgUrl = matchingVariant.featured_image.src.replace(/\.(jpg|jpeg|png|gif|webp)/, '_80x80.$1')
          imageContainer.innerHTML = `<img src="${imgUrl}" alt="${optionValue}" width="80" height="80" loading="lazy">`
        } else {
          // 如果没有找到匹配的变体图片，显示颜色块
          imageContainer.innerHTML = `<span style="background-color: ${optionValue.toLowerCase()}; display: block; width: 100%; height: 100%; border-radius: 50%;"></span>`
        }
      })
    })
  }

  adjustPricing (selectedVariant) {
    if (!selectedVariant) return

    const comparePrice = this.form.querySelector('.product-price-compare')
    const finalPrice = this.form.querySelector('.product-price-final')
    const compareBadge = this.form.querySelector('.product-sale-badge')
    const soldOutBadge = this.form.querySelector('.product-sold-out-badge')

    if (selectedVariant.compare_at_price > selectedVariant.price) {
      comparePrice.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price)
      comparePrice.removeAttribute('hidden')
      finalPrice.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.sale_price}}</span>${window.Shopify.formatMoney(selectedVariant.price)}`
      finalPrice.classList.add('product-price-final-sale')
      compareBadge.removeAttribute('hidden')
      if (compareBadge.dataset.discountType === 'percentage') {
        const savings = 100 - (Math.round(selectedVariant.price / selectedVariant.compare_at_price * 100)) + '%'
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      } else {
        const savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price)
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      }
    } else {
      comparePrice.textContent = ''
      comparePrice.setAttribute('hidden', 'hidden')
      finalPrice.innerHTML = window.Shopify.formatMoney(selectedVariant.price)
      finalPrice.classList.remove('product-price-final-sale')
      compareBadge.textContent = ''
      compareBadge.setAttribute('hidden', 'hidden')
    }

    if (selectedVariant.available) {
      soldOutBadge.setAttribute('hidden', 'hidden')
    } else {
      soldOutBadge.removeAttribute('hidden')
    }
  }

  adjustButtons (selectedVariant) {
    if (selectedVariant) {
      if (selectedVariant.available) {
        this.atcBtn.removeAttribute('disabled')
        this.buyBtn?.removeAttribute('disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.add_to_cart
      } else {
        this.atcBtn.setAttribute('disabled', 'disabled')
        this.buyBtn?.setAttribute('disabled', 'disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.sold_out
      }
    } else {
      this.atcBtn.setAttribute('disabled', 'disabled')
      this.buyBtn?.setAttribute('disabled', 'disabled')
      this.atcBtn.innerHTML = window.theme.locales.product.unavailable
    }
  }

  async onSubmit (event) {
    event.preventDefault()

    this.atcBtn.classList.add('loading')
    this.atcBtn.disabled = true
    this.atcBtn.setAttribute('aria-busy', 'true')

    const formData = new FormData(this.form)

    await document.querySelector('cart-container').add(formData)

    this.atcBtn.classList.remove('loading')
    this.atcBtn.disabled = false
    this.atcBtn.setAttribute('aria-busy', 'false')
  }

  onClickBuyBtn () {
    this.buyBtn.classList.add('loading')
    this.buyBtn.disabled = true
    this.buyBtn.setAttribute('aria-busy', 'true')

    // 保证礼品卡“收件人模式”时，控制字段与时区偏移被正确设置
    const giftForm = this.querySelector('gift-card-recipient-form')
    if (giftForm) {
      const recipientMode = giftForm.querySelector('[ref="recipientEmailButton"]')?.checked
      const controlFlag = giftForm.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]')
      const tz = giftForm.querySelector('input[name="properties[__shopify_offset]"]')

      const email = giftForm.querySelector('[ref="recipientEmail"]')
      const name = giftForm.querySelector('[ref="recipientName"]')
      const message = giftForm.querySelector('[ref="recipientMessage"]')
      const sendOn = giftForm.querySelector('[ref="recipientSendOn"]')

      if (recipientMode) {
        if (controlFlag) { controlFlag.disabled = false; controlFlag.value = 'on' }
        if (tz) { tz.disabled = false; tz.value = String(new Date().getTimezoneOffset()) }
        email?.removeAttribute('disabled');
        name?.removeAttribute('disabled');
        message?.removeAttribute('disabled');
        sendOn?.removeAttribute('disabled');
      } else {
        if (controlFlag) { controlFlag.disabled = true; controlFlag.value = '' }
        if (tz) { tz.disabled = true; tz.value = '' }
        email?.setAttribute('disabled', 'disabled');
        name?.setAttribute('disabled', 'disabled');
        message?.setAttribute('disabled', 'disabled');
        sendOn?.setAttribute('disabled', 'disabled');
      }
    }

    const formData = new FormData(this.form)

    // 直接结账遵循“只购买当前商品”的语义：清空购物车后添加当前商品，再进入结账
    fetch('/cart/clear.js', { method: 'POST' })
      .catch(() => null)
      .then(() => fetch(`${window.Shopify.routes.cart_add_url}.js`, { method: 'POST', body: formData }))
      .catch(() => null)
      .then(() => {
        window.location.href = '/checkout'
      })
  }
}
customElements.define('product-form', ProductForm)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    const galleryLayout = this.dataset.galleryLayout || 'thumbnail'
    const initialSlide = Number(this.dataset.initialSlide)
    
    // 保存原始HTML，用于恢复默认图片
    this.originalMainHTML = this.querySelector('.swiper-main .swiper-wrapper')?.innerHTML || '';
    this.originalThumbsHTML = this.querySelector('.swiper-thumbs .swiper-wrapper')?.innerHTML || '';

    // 堆叠布局模式下，桌面端不初始化 Swiper，移动端初始化
    if (galleryLayout === 'stacked') {
      // 移动端的 Swiper 元素在 .product-media-mobile 容器内
      const mobileContainer = this.querySelector('.product-media-mobile')
      if (mobileContainer) {
        const mobileThumbs = mobileContainer.querySelector('.swiper-thumbs')
        const mobileMain = mobileContainer.querySelector('.swiper-main')
        
        if (mobileThumbs && mobileMain) {
          /** @type {Swiper} */
          this.thumbs = new window.Swiper(mobileThumbs, this.thumbsOptions())

          /** @type {Swiper} */
          this.main = new window.Swiper(mobileMain, this.mainOptions())

          this.main.slideTo(initialSlide)
          this.loadInitialVariantImages()
          this.listenVariantChange()
        }
      }
      
      this.setStickyPosition()
      return
    }

    /** @type {Swiper} */
    this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions())

    /** @type {Swiper} */
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions())

    this.main.slideTo(initialSlide)

    this.setStickyPosition()
    this.loadInitialVariantImages()
    this.listenVariantChange()
  }
  
  loadInitialVariantImages() {
    // 获取当前选中的变体ID
    const productForm = document.querySelector('product-form');
    if (!productForm) return;
    
    const variantIdInput = productForm.querySelector('[name="id"]');
    if (!variantIdInput || !variantIdInput.value) return; // 如果没有选择变体，不加载
    
    const currentVariantId = variantIdInput.value;
    
    // 获取变体图片数据
    const variantImagesData = document.getElementById('variant-images-data');
    if (!variantImagesData) return;
    
    try {
      const variantImages = JSON.parse(variantImagesData.textContent);
      const customImages = variantImages[currentVariantId];
      
      if (customImages && customImages.length > 0) {
        // 如果当前变体有自定义图片，立即加载
        this.updateGalleryWithCustomImages(customImages);
      }
    } catch (e) {
      console.error('Failed to load initial variant images:', e);
    }
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: Number(this.dataset.thumbsPerView),
      spaceBetween: 10
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: 'bullets',
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      }
    }
    return options
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')

      let oldScroll = window.scrollY

      window.addEventListener('scroll', () => {
        const newScroll = window.scrollY
        if (newScroll > oldScroll) {
          if (newScroll > headerGroup.clientHeight) {
            this.style.top = '20px'
          }
        } else if (newScroll < oldScroll) {
          this.style.top = `${headerGroup.clientHeight + 20}px`
        }

        oldScroll = Math.max(window.scrollY, 0)
      })
    }
  }

  listenVariantChange () {
    // 获取变体图片数据
    const variantImagesData = document.getElementById('variant-images-data');
    let variantImages = {};
    if (variantImagesData) {
      try {
        variantImages = JSON.parse(variantImagesData.textContent);
      } catch (e) {
        console.error('Failed to parse variant images data:', e);
      }
    }
    
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail;
      
      // 检查该变体是否有自定义图片列表
      const customImages = variantImages[selectedVariant.id];
      
      if (customImages && customImages.length > 0) {
        // 使用自定义图片列表重建轮播
        this.updateGalleryWithCustomImages(customImages);
      } else {
        // 没有自定义图片，恢复默认产品图片
        this.restoreDefaultGallery(selectedVariant);
      }
    }, false);
  }
  
  restoreDefaultGallery(selectedVariant) {
    if (!this.main) return;
    
    // 销毁现有的 Swiper
    this.main.destroy(true, true);
    if (this.thumbs) {
      this.thumbs.destroy(true, true);
    }
    
    // 恢复原始HTML
    const mainWrapper = this.querySelector('.swiper-main .swiper-wrapper');
    if (mainWrapper && this.originalMainHTML) {
      mainWrapper.innerHTML = this.originalMainHTML;
    }
    
    const thumbsWrapper = this.querySelector('.swiper-thumbs .swiper-wrapper');
    if (thumbsWrapper && this.originalThumbsHTML) {
      thumbsWrapper.innerHTML = this.originalThumbsHTML;
      
      // 重新初始化缩略图 Swiper
      this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions());
    }
    
    // 重新初始化主 Swiper
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions());
    
    // 如果变体有关联图片，跳转到对应位置
    if (selectedVariant.featured_media) {
      const targetPosition = selectedVariant.featured_media.position - 1;
      this.main.slideTo(targetPosition);
    } else {
      this.main.slideTo(0);
    }
  }
  
  updateGalleryWithCustomImages(images) {
    if (!this.main) return;
    
    // 销毁现有的 Swiper
    this.main.destroy(true, true);
    if (this.thumbs) {
      this.thumbs.destroy(true, true);
    }
    
    // 更新主轮播的 HTML
    const mainWrapper = this.querySelector('.swiper-main .swiper-wrapper');
    if (mainWrapper) {
      mainWrapper.innerHTML = images.map(img => `
        <div class="swiper-slide">
          <img src="${img.src}" alt="${img.alt}" width="${img.width}" height="${img.height}" loading="lazy" class="img-fluid">
        </div>
      `).join('');
    }
    
    // 更新缩略图的 HTML
    const thumbsWrapper = this.querySelector('.swiper-thumbs .swiper-wrapper');
    if (thumbsWrapper) {
      thumbsWrapper.innerHTML = images.map(img => `
        <div class="swiper-slide">
          <img src="${img.src}" alt="${img.alt}" width="100" height="100" loading="lazy" class="img-thumbnail">
        </div>
      `).join('');
      
      // 重新初始化缩略图 Swiper
      this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions());
    }
    
    // 重新初始化主 Swiper
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions());
    
    // 跳转到第一张图片
    this.main.slideTo(0);
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)
